import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import { CatalogItemsDocument, type CatalogItemCoreFragment } from "@/graphql/generated/operations";
import { exactMatch, searchCatalogue } from "@/lib/games/toplist/search";
import {
  availableTags,
  backToRounds,
  backToTags,
  canSkipRound,
  choose,
  clearTags,
  createToplistSnapshot,
  isOwnEntryId,
  moveInResult,
  parseToplistSnapshot,
  skipRound,
  startDiscover,
  suggestionPool,
  takeOwn,
  toggleTag,
  TOPLIST_ROUND_SIZE,
  toplistEntries,
  unchoose,
  type ToplistSnapshot,
} from "@/lib/games/toplist/snapshot";
import { listGenus } from "@/lib/games/toplist/genus";
import { firstTagHue, releaseYearLabel, tagHues, tagLabels } from "@/lib/games/toplist/tags";
import type { GameProps } from "@/lib/games/types";
import { localizedText } from "@/lib/questionnaire-group";
import { userErrorMessage } from "@/lib/user-error";
import { DiscoverStage } from "./DiscoverStage";
import type { ToplistItem } from "./ToplistCard";
import { ToplistResult } from "./ToplistResult";
import type { ToplistSearchSource } from "./ToplistSearch";
import { TagsStage } from "./TagsStage";

// Search hits shown at once; the catalogue order puts the familiar ones first.
// The hits take the place of the round (or of what was passed over), so a round's worth.
const SEARCH_LIMIT = TOPLIST_ROUND_SIZE;

// The toplist game as Grow · Play sees it: a black box that loads its
// catalogue, walks through categories, rounds and result, reports its
// snapshot after every step and finishes with the ranked entries.
export function ToplistGame(props: GameProps) {
  const intl = useIntl();
  const { data, loading, error } = useQuery(CatalogItemsDocument, {
    variables: { input: { domain: props.target.domain } },
  });

  if (!data) {
    return (
      <p className="text-smycles-body text-ink-muted">
        {loading ? (
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        ) : error ? (
          userErrorMessage(intl, error)
        ) : null}
      </p>
    );
  }
  // Fewer entries than places and the list could never be finished.
  if (data.catalogItems.length < props.rules.keep) {
    return (
      <p className="text-smycles-body text-ink-muted">
        <FormattedMessage
          id="grow.games.toplist.catalogueTooSmall"
          defaultMessage="There are not enough entries for this game yet."
        />
      </p>
    );
  }
  return <ToplistPlay {...props} catalogue={data.catalogItems} />;
}

// Mounted once the catalogue is there, so the game state can start from it.
function ToplistPlay({
  catalogue,
  rules,
  target,
  question,
  snapshot: restored,
  onProgress,
  onFinish,
  onRestart,
}: GameProps & { catalogue: readonly CatalogItemCoreFragment[] }) {
  const intl = useIntl();
  const catalogueCards = useMemo(() => {
    const hues = tagHues(intl, target.domain);
    return new Map<string, ToplistItem>(
      catalogue.map((item) => [
        item.id,
        {
          id: item.id,
          title: localizedText(item.title),
          // Only films, series and books have a year; nothing else shows a subtitle.
          subtitle: releaseYearLabel(intl, item.releaseYear),
          labels: tagLabels(intl, item.domain, item.tags),
          hue: firstTagHue(hues, item.tags),
        },
      ]),
    );
  }, [catalogue, intl, target.domain]);
  const tags = useMemo(
    () =>
      availableTags(catalogue).flatMap((code) =>
        tagLabels(intl, target.domain, [code]).map((label) => ({ code, label })),
      ),
    [catalogue, intl, target.domain],
  );
  const catalogueEntries = useMemo(
    () => catalogue.map((item) => ({ id: item.id, texts: [item.title.localized, item.title.german, item.title.english] })),
    [catalogue],
  );
  const [snapshot, setSnapshot] = useState(() => initialSnapshot(restored, catalogueCards, rules.keep));
  const pool = useMemo(() => suggestionPool(catalogue, snapshot.tags), [catalogue, snapshot.tags]);
  // Own titles are cards and search entries like the catalogue's: marked as the
  // person's own, coloured by their id, and found again instead of typed twice.
  const cards = useMemo(() => {
    const ownLabel = intl.formatMessage({ id: "grow.games.toplist.ownEntry", defaultMessage: "Your own entry" });
    const own = snapshot.own.map((entry): [string, ToplistItem] => [entry.id, { ...entry, subtitle: ownLabel }]);
    return new Map([...catalogueCards, ...own]);
  }, [catalogueCards, intl, snapshot.own]);
  const searchEntries = useMemo(
    () => [...catalogueEntries, ...snapshot.own.map((entry) => ({ id: entry.id, texts: [entry.title] }))],
    [catalogueEntries, snapshot.own],
  );
  const cardsOf = (ids: readonly string[]) => ids.flatMap((id) => cards.get(id) ?? []);

  const step = (next: ToplistSnapshot) => {
    if (next === snapshot) return;
    setSnapshot(next);
    onProgress(next, { checkpoint: next.phase !== snapshot.phase });
  };
  const search: ToplistSearchSource = {
    find: (query) => ({
      hits: cardsOf(searchCatalogue(searchEntries, query, SEARCH_LIMIT)),
      exact: exactMatch(searchEntries, query) !== null,
    }),
    placeholder: intl.formatMessage({ id: `grow.games.toplist.search.placeholder.${target.domain}` }),
    genus: listGenus(intl, target.domain),
    onTakeOwn: (title) => step(takeOwn(snapshot, title, rules.keep)),
  };

  switch (snapshot.phase) {
    case "tags":
      return (
        <TagsStage
          question={intl.formatMessage({ id: `grow.games.toplist.tags.question.${target.domain}` })}
          tags={tags}
          chosen={snapshot.tags}
          onToggle={(code) => step(toggleTag(snapshot, code))}
          onAll={() => step(clearTags(snapshot))}
          onNext={() => step(startDiscover(snapshot, pool))}
        />
      );
    case "discover":
      return (
        <DiscoverStage
          question={question}
          round={cardsOf(snapshot.round)}
          picks={cardsOf(snapshot.selected)}
          keep={rules.keep}
          canSkip={canSkipRound(snapshot)}
          search={search}
          onChoose={(id) => step(choose(snapshot, id, rules.keep))}
          onSkip={() => step(skipRound(snapshot))}
          onRemove={(id) => step(unchoose(snapshot, id))}
          onChangeTags={() => step(backToTags(snapshot))}
        />
      );
    case "result":
      return (
        <ToplistResult
          items={cardsOf(snapshot.selected)}
          keep={rules.keep}
          passed={cardsOf(snapshot.passed)}
          search={search}
          onMove={(from, to) => step(moveInResult(snapshot, from, to))}
          onRemove={(id) => step(unchoose(snapshot, id))}
          onAdd={(id) => step(choose(snapshot, id, rules.keep))}
          onMoreRounds={() => step(backToRounds(snapshot, rules.keep))}
          onApply={() => {
            const entries = toplistEntries(snapshot, rules.keep);
            // "Save" is only offered with every place filled; this keeps a short list from ever being written.
            return entries ? onFinish({ entries }) : Promise.reject(new Error("The ranking is not complete."));
          }}
          onRestart={onRestart}
        />
      );
  }
}

// Resumes the stored game when every entry it refers to is still offered;
// otherwise a new game.
function initialSnapshot(
  restored: Record<string, unknown> | null,
  cards: ReadonlyMap<string, ToplistItem>,
  keep: number,
): ToplistSnapshot {
  const parsed = restored ? parseToplistSnapshot(restored, keep) : null;
  // An own title needs no catalogue entry: the snapshot carries it.
  const known = (ids: readonly string[]) => ids.every((id) => isOwnEntryId(id) || cards.has(id));
  if (parsed && known(parsed.round) && known(parsed.queue) && known(parsed.selected)) return parsed;
  return createToplistSnapshot();
}
